import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Reading {
  subject: string;
}

interface SubjectChartProps {
  readings: Reading[];
}

const SubjectChart: React.FC<SubjectChartProps> = ({ readings }) => {
  const subjectData = useMemo(() => {
    const subjectCounts = readings.reduce((acc: { [key: string]: number }, reading) => {
      const subject = reading.subject.trim();
      acc[subject] = (acc[subject] || 0) + 1;
      return acc;
    }, {});

    // Sort by count in descending order
    const sortedSubjects = Object.entries(subjectCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10); // Show top 10 subjects

    return {
      labels: sortedSubjects.map(([subject]) => subject),
      counts: sortedSubjects.map(([, count]) => count)
    };
  }, [readings]);

  const data = {
    labels: subjectData.labels,
    datasets: [
      {
        label: 'Number of Readings',
        data: subjectData.counts,
        backgroundColor: 'rgba(147, 51, 234, 0.5)',
        borderColor: 'rgb(147, 51, 234)',
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: 'rgba(147, 51, 234, 0.7)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1a1a1a',
        bodyColor: '#1a1a1a',
        borderColor: 'rgba(147, 51, 234, 0.2)',
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          label: (context: any) => {
            return `${context.raw} reading${context.raw !== 1 ? 's' : ''}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0,
        },
        grid: {
          color: 'rgba(147, 51, 234, 0.1)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  return (
    <div className="h-[300px] w-full">
      <Bar data={data} options={options} />
    </div>
  );
};

export default SubjectChart;